import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { X, KeyRound, Send, SquareArrowUp, Compass, Filter, Hash, Type, Pin, Users, Flag, Video, Megaphone, Sparkles, Bell, Info, Radio, Layers, Palette, Eye, Download, ShieldCheck, MessageCircle, Film, CalendarClock, Flame } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";

const ICONS = { KeyRound, Send, SquareArrowUp, Compass, Filter, Hash, Type, Pin, Users, Flag, Video, Megaphone, Sparkles, Bell, Info, Radio, Layers, Palette, Eye, Download, ShieldCheck, MessageCircle, Film, CalendarClock, Flame };
export const TipIcon = ({ name, ...p }) => { const C = ICONS[name] || Info; return <C {...p} />; };

// Render **bold** emphasis inside tip body text.
const RichBody = ({ text }) => (
  <>{String(text || "").split("**").map((seg, i) => (i % 2 ? <span key={i} className="font-semibold text-zinc-900">{seg}</span> : <React.Fragment key={i}>{seg}</React.Fragment>))}</>
);

// Server-driven tip config (content + per-tip rev), fetched once per session.
const TipsContext = createContext({ tips: [], reloadTips: () => {} });

export const TipsProvider = ({ children }) => {
  const { user } = useAuth();
  const [tips, setTips] = useState([]);
  const reloadTips = useCallback(async () => {
    try { const { data } = await api.get("/tips/config"); setTips(data || []); } catch { /* not logged in yet */ }
  }, []);
  useEffect(() => { if (user) reloadTips(); }, [user, reloadTips]);
  return <TipsContext.Provider value={{ tips, reloadTips }}>{children}</TipsContext.Provider>;
};

export const useTips = () => useContext(TipsContext);

// The single active (highest-priority unseen) tip id for a screen, used to visually
// cue the matching UI element (e.g. glow Transmit while its tip is showing).
export const useActiveScreenTip = (screen) => {
  const { user } = useAuth();
  const { tips } = useTips();
  if (!user || !tips.length) return null;
  const seen = new Set(user.dismissed_tips || []);
  const t = tips.filter((x) => x.screen === screen).find((x) => !seen.has(`${x.tip_id}@${x.rev}`));
  return t ? t.tip_id : null;
};

// Whether a specific tip is still unseen (used to glow the element it points at).
export const useTipUnseen = (id) => {
  const { user } = useAuth();
  const { tips } = useTips();
  const t = tips.find((x) => x.tip_id === id);
  return !!(user && t && !(user.dismissed_tips || []).includes(`${t.tip_id}@${t.rev}`));
};

const CouncilAnnouncement = ({ tip, onDismiss }) => (
  <div className="relative mb-6 rounded-xl overflow-hidden bg-[#0E1413] border border-[#14A89A]/40 shadow-xl shadow-black/60 p-5 pr-12 fade-up" data-testid={`council-announcement-${tip.tip_id}`}>
    <div className="absolute inset-y-0 left-0 w-1 bg-gradient-to-b from-[#14A89A] to-[#8B5CF6]" />
    <div className="absolute -top-16 -right-16 w-48 h-48 rounded-full bg-[#8B5CF6]/15 blur-3xl pointer-events-none" />
    <button onClick={onDismiss} aria-label="Dismiss" data-testid={`council-announcement-close-${tip.tip_id}`} className="absolute top-3 right-3 w-7 h-7 rounded-full bg-white/5 hover:bg-white/10 flex items-center justify-center text-zinc-400 hover:text-white transition-colors">
      <X size={16} strokeWidth={2.25} />
    </button>
    <div className="relative flex items-start gap-3">
      <div className="shrink-0 w-10 h-10 rounded-lg flex items-center justify-center bg-[#14A89A]/15 text-[#2BC4B5] ring-1 ring-[#8B5CF6]/30">
        <Megaphone size={18} strokeWidth={2} />
      </div>
      <div className="min-w-0 flex-1">
        <div className="font-body text-[10px] font-semibold tracking-[0.25em] uppercase mb-1.5 text-[#2BC4B5]" data-testid="council-announcement-label">Council Announcement</div>
        {tip.title && <div className="font-heading text-base text-white mb-1">{tip.title}</div>}
        <div className="font-body text-xs text-zinc-300 leading-relaxed">{String(tip.body || "").split("**").map((seg, i) => (i % 2 ? <span key={i} className="font-semibold text-[#C4B5FD]">{seg}</span> : <React.Fragment key={i}>{seg}</React.Fragment>))}</div>
        <button onClick={onDismiss} data-testid={`council-announcement-dismiss-${tip.tip_id}`} className="mt-4 inline-flex items-center font-body text-[11px] tracking-[0.15em] uppercase font-semibold text-[#0A0A0A] bg-[#14A89A] hover:bg-[#2BC4B5] px-3.5 py-1.5 rounded-md transition-colors">Got it</button>
      </div>
    </div>
  </div>
);

// Show the single highest-priority unseen tip for a screen (or an explicit ordered id list).
// A tip is "seen" only for its current revision, editing one tip re-shows just that tip.
export const CoachTips = ({ screen, ids }) => {
  const { user, dismissTip } = useAuth();
  const { tips } = useTips();
  if (!user || !tips.length) return null;
  const seen = new Set(user.dismissed_tips || []);
  let candidates;
  if (ids) {
    candidates = ids.map((id) => tips.find((t) => t.tip_id === id)).filter(Boolean);
  } else {
    candidates = tips.filter((t) => t.screen === screen); // already priority-sorted by server
  }
  const tip = candidates.find((t) => !seen.has(`${t.tip_id}@${t.rev}`));
  if (!tip) return null;
  if (tip.builtin === false && tip.screen === "feed") return <CouncilAnnouncement tip={tip} onDismiss={() => dismissTip(tip.tip_id, tip.rev)} />;
  return (
    <div className="relative mb-6 rounded-xl bg-white border-l-4 shadow-xl shadow-black/50 ring-1 ring-black/5 p-4 pr-11 fade-up" style={{ borderLeftColor: tip.color || "#14A89A" }} data-testid={`coach-tip-${tip.tip_id}`}>
      <button onClick={() => dismissTip(tip.tip_id, tip.rev)} aria-label="Dismiss" data-testid={`coach-tip-close-${tip.tip_id}`} className="absolute top-2.5 right-2.5 w-7 h-7 rounded-full bg-zinc-100 hover:bg-zinc-200 flex items-center justify-center text-zinc-500 hover:text-zinc-900 transition-colors">
        <X size={16} strokeWidth={2.25} />
      </button>
      <div className="flex items-start gap-3">
        <div className="shrink-0 w-9 h-9 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${tip.color || "#14A89A"}1A`, color: tip.color || "#14A89A" }}>
          <TipIcon name={tip.icon} size={18} strokeWidth={2} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="font-body text-[10px] font-semibold tracking-[0.22em] uppercase mb-1" style={{ color: tip.color || "#14A89A" }}>Tip</div>
          {tip.title && <div className="font-heading text-sm text-zinc-900 mb-1">{tip.title}</div>}
          <div className="font-body text-xs text-zinc-600 leading-relaxed"><RichBody text={tip.body} /></div>
          <button onClick={() => dismissTip(tip.tip_id, tip.rev)} data-testid={`coach-tip-dismiss-${tip.tip_id}`} className="mt-3 inline-flex items-center font-body text-[11px] tracking-[0.15em] uppercase font-semibold text-[#0A0A0A] bg-[#14A89A] hover:bg-[#2BC4B5] px-3 py-1.5 rounded-md transition-colors">Got it</button>
        </div>
      </div>
    </div>
  );
};
